import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTodayPuzzles } from '../services/api';
import './Landing.css';

const GAMES = [
  {
    key: 'word',
    icon: '📝',
    name: 'WordForge',
    tag: 'FREE DAILY',
    desc: 'Guess the 5-letter word in 6 tries. Each week has a theme — Space, Nature, History and more.',
    preview: ['⬛🟨🟩⬛⬛', '🟩🟩🟩🟨⬛', '🟩🟩🟩🟩🟩'],
    color: '#4fe3c1',
  },
  {
    key: 'story',
    icon: '📖',
    name: 'StoryForge',
    tag: 'AI POWERED',
    desc: 'Read an AI-generated micro-story and fill in the missing word. Progressive hints reveal on each wrong guess.',
    preview: ['"The ancient trees swayed as the last', '____ fell from the sky, marking the', 'end of a thousand-year drought."'],
    color: '#e8c547',
  },
  {
    key: 'cipher',
    icon: '🔐',
    name: 'CipherForge',
    tag: 'AI POWERED',
    desc: 'Decode an encrypted secret message. Map each letter to crack the cipher before your reveals run out.',
    preview: ['CMUOC NVMGO AQKJ', '→ ? ? ? ? ? ? ? ?', 'M=I, C=F, O=R...'],
    color: '#a78bfa',
  },
  {
    key: 'trivia',
    icon: '🧠',
    name: 'TriviaForge',
    tag: 'AI POWERED',
    desc: '5 questions on a fresh AI-generated topic every day. Each question harder than the last.',
    preview: ['Topic: Ocean Life 🌊', 'Q1 ✓  Q2 ✓  Q3 ✓', 'Q4 ✗  Q5 ✓  → 4/5'],
    color: '#f472b6',
  },
  {
    key: 'logic',
    icon: '🧩',
    name: 'LogicForge',
    tag: 'AI POWERED',
    desc: 'Match 4 people to their roles and locations using deductive clues. AI writes thematic clues for every puzzle.',
    preview: ['Victor → Butler → Library', 'Helena → Cook → Kitchen', '3 tries · 2m 14s ✅'],
    color: '#fb923c',
  },
];

const STATS = [
  { value: '5', label: 'AI-powered games' },
  { value: '12', label: 'weekly themes' },
  { value: '∞', label: 'unique puzzles' },
  { value: '100%', label: 'free to start' },
];

export default function Landing() {
  const [theme, setTheme] = useState({ name: 'Daily Puzzles', emoji: '🧩' });
  const [activeGame, setActiveGame] = useState(0);

  useEffect(() => {
    getTodayPuzzles().then(r => {
      const word = r.data.find(p => p.puzzle_type === 'WORD');
      if (word?.content?.theme) {
        setTheme({ name: word.content.theme, emoji: word.content.theme_emoji || '🧩' });
      }
    }).catch(() => {});

    // Auto-rotate games
    const interval = setInterval(() => {
      setActiveGame(g => (g + 1) % GAMES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="landing">

      {/* ── Hero ── */}
      <section className="landing-hero">
        <div className="landing-theme-pill mono">
          {theme.emoji} This week: {theme.name}
        </div>
        <h1 className="landing-title">
          Daily puzzles.<br />
          <span className="landing-title-accent">Powered by AI.</span>
        </h1>
        <p className="landing-subtitle">
          5 unique AI-generated puzzles every day. Fresh content, weekly themes, progressive hints — never the same puzzle twice.
        </p>
        <div className="landing-cta-row">
          <Link to="/register" className="landing-cta-primary mono">
            Play free today →
          </Link>
          <Link to="/login" className="landing-cta-secondary mono">
            Sign in
          </Link>
        </div>
        <p className="landing-free-note mono">Free · No credit card · 2 plays per game per day</p>
      </section>

      {/* ── Stats ── */}
      <section className="landing-stats">
        {STATS.map((s, i) => (
          <div key={i} className="landing-stat">
            <div className="landing-stat-value mono">{s.value}</div>
            <div className="landing-stat-label mono">{s.label}</div>
          </div>
        ))}
      </section>

      {/* ── Games ── */}
      <section className="landing-games">
        <h2 className="landing-section-title mono">5 games. All AI-powered.</h2>
        <div className="landing-games-tabs">
          {GAMES.map((g, i) => (
            <button
              key={g.key}
              className={`landing-game-tab mono ${activeGame === i ? 'active' : ''}`}
              style={activeGame === i ? { borderColor: g.color, color: g.color } : {}}
              onClick={() => setActiveGame(i)}
            >
              {g.icon} {g.name}
            </button>
          ))}
        </div>

        {GAMES.map((g, i) => (
          <div key={g.key} className={`landing-game-card ${activeGame === i ? 'active' : ''}`}>
            <div className="landing-game-header">
              <span className="landing-game-icon">{g.icon}</span>
              <div>
                <span className="landing-game-tag mono" style={{ color: g.color, borderColor: g.color, background: `${g.color}15` }}>{g.tag}</span>
                <h3 className="landing-game-name mono">{g.name}</h3>
              </div>
            </div>
            <p className="landing-game-desc">{g.desc}</p>
            <div className="landing-game-preview">
              {g.preview.map((line, j) => (
                <div key={j} className="landing-preview-line mono">{line}</div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* ── How it works ── */}
      <section className="landing-how">
        <h2 className="landing-section-title mono">How it works</h2>
        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step-num mono">01</div>
            <div className="landing-step-title mono">Sign up free</div>
            <div className="landing-step-desc">Create an account in seconds. No credit card needed.</div>
          </div>
          <div className="landing-step-arrow">→</div>
          <div className="landing-step">
            <div className="landing-step-num mono">02</div>
            <div className="landing-step-title mono">Play daily</div>
            <div className="landing-step-desc">2 free plays per game type every day. Fresh AI puzzles each morning.</div>
          </div>
          <div className="landing-step-arrow">→</div>
          <div className="landing-step">
            <div className="landing-step-num mono">03</div>
            <div className="landing-step-title mono">Get more</div>
            <div className="landing-step-desc">Buy credits for more plays. Never expire, use anytime.</div>
          </div>
        </div>
      </section>

      {/* ── Pricing teaser ── */}
      <section className="landing-pricing">
        <h2 className="landing-section-title mono">Simple pricing</h2>
        <div className="landing-plans">
          <div className="landing-plan">
            <div className="landing-plan-name mono">Free</div>
            <div className="landing-plan-price mono">$0</div>
            <div className="landing-plan-desc">2 plays per game per day</div>
            <Link to="/register" className="landing-plan-btn mono">Start free</Link>
          </div>
          <div className="landing-plan">
            <div className="landing-plan-name mono">Starter</div>
            <div className="landing-plan-price mono">$0.99</div>
            <div className="landing-plan-desc">10 credits · never expire</div>
            <Link to="/register" className="landing-plan-btn mono">Get started</Link>
          </div>
          <div className="landing-plan featured">
            <div className="landing-plan-badge mono">BEST VALUE</div>
            <div className="landing-plan-name mono">Value Pack</div>
            <div className="landing-plan-price mono">$5.99</div>
            <div className="landing-plan-desc">100 credits · never expire</div>
            <Link to="/register" className="landing-plan-btn featured mono">Get Value Pack</Link>
          </div>

        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="landing-final">
        <h2 className="landing-final-title mono">Ready to play?</h2>
        <p className="landing-final-sub">Play fresh AI puzzles every day. Free to start, no credit card required.</p>
        <Link to="/register" className="landing-cta-primary mono large">
          Create free account →
        </Link>
        <p className="landing-free-note mono" style={{marginTop:'12px'}}>
          Already have an account? <Link to="/login" style={{color:'#4fe3c1'}}>Sign in</Link>
        </p>
      </section>

    </div>
  );
}
